/**
 * THE FILE — the three columns every job screen shares: spine · file · dock.
 *
 * The spatial model is a file open on a desk. The SPINE is the index down the
 * left: which part of the file you are reading, and the professional position
 * stated once so it is never off screen. THE FILE is the reading column, and it
 * keeps its measure at every width — a 900px column of text is readable and a
 * 2,000px one is not, so the layout never spends a wide monitor on longer
 * lines. The DOCK is the other thing you would keep open beside the file: the
 * workpaper while you read the evidence, the evidence while you read the
 * workpaper.
 *
 * The dock column EXISTS ONLY WHEN THERE IS SOMETHING TO PUT IN IT. An empty
 * third column is a promise the product has not kept; the grid simply drops to
 * two.
 */
import { clsx } from "clsx";
import type { ReactNode } from "react";
import { Label, SERIF_OPSZ } from "./text.js";

export interface FileLayoutProps {
  spine: ReactNode;
  /** Omit it and the third column does not exist at any width. */
  dock?: ReactNode;
  /** `wide` (1100px) is for the desk screens before a file is open. */
  measure?: "file" | "wide" | undefined;
  children: ReactNode;
}

/**
 *   ≥1280 (xl)  spine · file · dock
 *   ≥1024 (lg)  spine · file
 *   <1024       the spine becomes a strip above the file
 *
 * min-h assumes the 56px top bar above it, so the spine's rule reaches the
 * bottom of the window on a short page instead of stopping mid-screen.
 */
export function FileLayout({ spine, dock, measure = "file", children }: FileLayoutProps) {
  return (
    <div
      className={clsx(
        "grid min-h-[calc(100vh-56px)] grid-cols-1 lg:grid-cols-[232px_minmax(0,1fr)]",
        dock && "xl:grid-cols-[232px_minmax(0,1fr)_300px]"
      )}
    >
      <nav aria-label="This file" className="border-b border-rule bg-canvas px-5 py-4 lg:border-b-0 lg:border-r lg:px-6 lg:py-8">
        {spine}
      </nav>
      <main className="min-w-0 px-5 py-8 md:px-10 lg:px-14 lg:py-10">
        <div className={measure === "wide" ? "max-w-desk" : "max-w-file"}>{children}</div>
      </main>
      {dock && (
        <aside aria-label="Dock" className="hidden border-l border-rule bg-raised px-8 py-10 xl:block">
          {dock}
        </aside>
      )}
    </div>
  );
}

export interface SpineProps {
  children: ReactNode;
  className?: string | undefined;
}

/** The spine's rhythm: a column at desk widths, a wrapping strip below them. */
export function Spine({ children, className }: SpineProps) {
  return (
    <div className={clsx("flex flex-row flex-wrap gap-x-7 gap-y-4 lg:flex-col lg:gap-y-7", className)}>
      {children}
    </div>
  );
}

export interface SpineGroupProps {
  label?: ReactNode;
  children: ReactNode;
  className?: string | undefined;
}

/** A labelled group inside the spine. */
export function SpineGroup({ label, children, className }: SpineGroupProps) {
  return (
    <div className={className}>
      {label && <Label className="mb-3">{label}</Label>}
      <div className="flex flex-row flex-wrap gap-x-5 gap-y-2 lg:flex-col lg:gap-y-2.5">{children}</div>
    </div>
  );
}

type SpineItemBase = {
  children: ReactNode;
  /** The section being read. Weight, not a background — the spine stays quiet. */
  current?: boolean | undefined;
  /** A count beside the name. Tabular, so a column of them lines up. */
  count?: number | null | undefined;
  /** The sub-index, shown only for the current section and only at desk widths. */
  sub?: ReactNode;
  className?: string | undefined;
};

type SpineItemProps =
  | (SpineItemBase & { as?: "button" | undefined; onSelect?: (() => void) | undefined })
  | (SpineItemBase & { as: "a"; href: string });

/**
 * One section of the file.
 *
 * `current` is weight and colour, never a filled background: the spine is an
 * index, and an index with a highlighted row starts competing with the file
 * for the reader's attention. The sub-index appears under the current item
 * only — showing every section's sub-index would put twenty links in a 232px
 * column and index nothing.
 */
export function SpineItem(props: SpineItemProps) {
  const { children, current, count, sub, className } = props;
  const cls = clsx("block text-left text-sm", current ? "font-semibold text-ink" : "text-ink-soft hover:text-ink", className);
  const body = (
    <>
      {children}
      {count !== undefined && count !== null && (
        <>
          {" "}
          <span className="ml-1 text-[13px] font-normal text-stone [font-variant-numeric:tabular-nums]">{count}</span>
        </>
      )}
    </>
  );
  return (
    <div>
      {props.as === "a" ? (
        <a href={props.href} aria-current={current ? "page" : undefined} className={cls}>{body}</a>
      ) : (
        <button type="button" onClick={props.onSelect} aria-current={current ? "true" : undefined} className={cls}>
          {body}
        </button>
      )}
      {current && sub && <div className="mt-2 hidden flex-col gap-1.5 pl-3.5 lg:flex">{sub}</div>}
    </div>
  );
}

export interface SpineSubProps {
  href: string;
  children: ReactNode;
}

/** An anchor within the current section — the sub-index under a spine item. */
export function SpineSub({ href, children }: SpineSubProps) {
  return <a href={href} className="text-[13px] text-stone hover:text-ink">{children}</a>;
}

export interface DockProps {
  label?: ReactNode;
  title?: ReactNode;
  meta?: ReactNode;
  children?: ReactNode;
  /** Text actions, set as words — the dock never carries the screen's one button. */
  actions?: ReactNode;
}

/**
 * The dock: one surface at a time, on the raised ground.
 *
 * It holds what you would keep open beside the file, so it is a reference
 * surface and not a second workspace — one heading, one body, and actions set
 * as words. The single saturated element on a screen belongs to the primary
 * action in the file; a filled button out here would steal it.
 */
export function Dock({ label, title, meta, children, actions }: DockProps) {
  return (
    <div className="flex flex-col gap-7">
      <div>
        {label && <Label>{label}</Label>}
        {title && (
          <div className="mt-2 font-serif text-[19px] font-medium leading-[1.3] text-ink" style={SERIF_OPSZ}>
            {title}
          </div>
        )}
        {meta && <div className="mt-1 text-[13px] text-stone">{meta}</div>}
        {children && <div className="mt-3.5">{children}</div>}
        {actions && <div className="mt-4 flex gap-3.5 text-[13px]">{actions}</div>}
      </div>
    </div>
  );
}

export interface AvatarProps {
  /** Initials only. This product does not upload, store or display a photograph. */
  initials: string;
  /** The signed-in person, on forest. Everyone else on the neutral avatar fill. */
  self?: boolean | undefined;
  size?: number | undefined;
}

/**
 * `aria-hidden` because initials are not a name: every place an avatar appears,
 * the person's name appears beside it, and a reader announcing "AO" would be
 * reading the abbreviation instead of the person.
 */
export function Avatar({ initials, self, size = 30 }: AvatarProps) {
  return (
    <span
      className={clsx(
        "inline-flex shrink-0 items-center justify-center rounded-full text-[12px] font-semibold",
        self ? "bg-forest text-forest-on" : "bg-avatar text-ink"
      )}
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      {initials}
    </span>
  );
}
